/** @jsxImportSource @emotion/react */
'use client'

import { css } from '@emotion/react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useRef } from 'react'
import { colorTokens } from '../../styles/tokens'
import { styles } from './styles'

/**
 * Visual treatment per row, applied by rank position (row 0 = the eventual
 * winner). Colours are stable across claims, so they live in the JSX rather
 * than being reset each cycle.
 */
const TIERS = [
  {
    avatarBg: colorTokens.green100,
    avatarColor: colorTokens.white,
    barColor: colorTokens.green100,
    pctColor: colorTokens.green100,
  },
  {
    avatarBg: '#d9e6d9',
    avatarColor: '#4f7a54',
    barColor: '#6f9c73',
    pctColor: colorTokens.green900,
  },
  {
    avatarBg: '#e2ece1',
    avatarColor: '#6f8c72',
    barColor: '#9cbf9f',
    pctColor: colorTokens.green900,
  },
] as const

interface Partner {
  name: string
  initials: string
  /** Final match score (%). */
  pct: number
  /** Speed profile — low `exp` surges early, high `exp` finishes strong. */
  exp: number
}

interface Claim {
  id: string
  type: string
  /** AI claim score shown in the ring. */
  score: number
  /** Exactly three partners, authored in descending `pct` (settle order). */
  partners: [Partner, Partner, Partner]
}

/**
 * The loop cycles through these claims — each fades in, is scored, its partners
 * race and reorder, it settles on "Matched", then fades out for the next one —
 * so the hero reads as a live stream of claims being auto-routed. Partners are
 * listed in final rank order; the differing `exp` values make them overtake one
 * another mid-build before settling back into that order.
 */
const CLAIMS: Claim[] = [
  {
    id: 'INC-4821',
    type: 'Motor',
    score: 92,
    partners: [
      { name: 'Northwood Claims', initials: 'NW', pct: 96, exp: 2.2 },
      { name: 'Aspect Partners', initials: 'AP', pct: 81, exp: 0.9 },
      { name: 'Meridian Resolve', initials: 'MR', pct: 74, exp: 0.5 },
    ],
  },
  {
    id: 'INC-4907',
    type: 'Property',
    score: 88,
    partners: [
      { name: 'Harbour Loss', initials: 'HL', pct: 93, exp: 2.4 },
      { name: 'Delta Adjusters', initials: 'DA', pct: 84, exp: 0.7 },
      { name: 'Coastal Claims', initials: 'CC', pct: 69, exp: 0.5 },
    ],
  },
  {
    id: 'INC-5033',
    type: 'Liability',
    score: 95,
    partners: [
      { name: 'Sterling Group', initials: 'SG', pct: 97, exp: 2.0 },
      { name: 'Vantage Partners', initials: 'VP', pct: 86, exp: 1.3 },
      { name: 'Oakridge Resolve', initials: 'OR', pct: 72, exp: 0.5 },
    ],
  },
]

const RING_R = 63
const RING_C = 2 * Math.PI * RING_R
const BUILD_S = 4.5 // scoring / race build-up
const HOLD_S = 1.4 // pause on the settled "Matched" state
const FADE_S = 0.55 // cross-fade between claims

const FIRST = CLAIMS[0]

const ARIA_LABEL =
  'Animated illustration of WyseHub scoring incoming insurance claims and ' +
  'routing each to its best-matched partner.'

export default function ClaimRoutingCard() {
  const rootRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<HTMLDivElement[]>([])
  const avatarRefs = useRef<HTMLDivElement[]>([])
  const nameRefs = useRef<HTMLDivElement[]>([])
  const fillRefs = useRef<HTMLDivElement[]>([])
  const pctRefs = useRef<HTMLSpanElement[]>([])
  const ringRef = useRef<SVGCircleElement>(null)
  const scoreRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const routedRef = useRef<HTMLSpanElement>(null)

  useGSAP(
    () => {
      const rows = rowRefs.current
      const fills = fillRefs.current
      const pcts = pctRefs.current
      const avatars = avatarRefs.current
      const names = nameRefs.current
      const content = contentRef.current
      const title = titleRef.current
      const ring = ringRef.current
      const scoreEl = scoreRef.current
      const statusEl = statusRef.current
      const routedEl = routedRef.current
      if (
        !content ||
        !title ||
        !ring ||
        !scoreEl ||
        !statusEl ||
        !routedEl ||
        rows.length < 3
      ) {
        return
      }

      // Base layout positions (transforms don't affect offsetTop), remeasured
      // on resize so the rank slide stays exact as the card scales.
      let baseTops = rows.map((r) => r.offsetTop)
      const measure = () => {
        baseTops = rows.map((r) => r.offsetTop)
      }

      // Swap in the static text for a claim (colours are fixed per row).
      const setClaim = (claim: Claim) => {
        title.textContent = `${claim.id} · ${claim.type}`
        claim.partners.forEach((p, i) => {
          avatars[i].textContent = p.initials
          names[i].textContent = p.name
        })
      }

      // Write the animated state for a claim at normalized progress p in [0, 1].
      // Driven imperatively (not via React state) so 60fps updates never
      // re-render or thrash Emotion — and never trip the inline-style lint,
      // which only matches JSX style/sx attributes.
      const apply = (claim: Claim, p: number) => {
        const vals = claim.partners.map((pt) => pt.pct * p ** pt.exp)
        const order = claim.partners
          .map((_, i) => i)
          .sort((a, b) => vals[b] - vals[a])
        const slot: number[] = []
        order.forEach((i, s) => {
          slot[i] = s
        })

        claim.partners.forEach((_, i) => {
          fills[i].style.width = `${vals[i].toFixed(2)}%`
          pcts[i].textContent = `${Math.round(vals[i])}%`
          rows[i].style.transform =
            `translateY(${baseTops[slot[i]] - baseTops[i]}px)`
        })

        // Score ring + count settle with an ease-out so the sweep decelerates.
        const rp = 1 - (1 - p) ** 3
        const dash = (claim.score / 100) * RING_C * rp
        ring.style.strokeDasharray = `${dash.toFixed(1)} ${RING_C.toFixed(1)}`
        scoreEl.textContent = String(Math.round(claim.score * rp))
        routedEl.textContent = (p * 1.2).toFixed(1)
        statusEl.textContent = p >= 1 ? 'Matched' : 'Matching…'
      }

      const mm = gsap.matchMedia()

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        measure()
        gsap.set(content, { autoAlpha: 0 })

        // One master timeline chains every claim and repeats forever. Each
        // claim: fade in → score + race build-up → hold on "Matched" → fade
        // out, so the reset is a cross-fade to a fresh claim, never a jump.
        const master = gsap.timeline({ repeat: -1 })
        for (const claim of CLAIMS) {
          const state = { p: 0 }
          master
            .call(() => {
              setClaim(claim)
              apply(claim, 0)
            })
            .fromTo(
              content,
              { autoAlpha: 0, y: 12 },
              { autoAlpha: 1, y: 0, duration: FADE_S, ease: 'power2.out' },
            )
            .to(state, {
              p: 1,
              duration: BUILD_S,
              ease: 'none',
              onUpdate: () => apply(claim, state.p),
            })
            .to({}, { duration: HOLD_S })
            .to(content, {
              autoAlpha: 0,
              y: -12,
              duration: FADE_S,
              ease: 'power2.in',
            })
        }

        // Only animate while on-screen and the tab is visible.
        let onScreen = true
        let tabVisible = !document.hidden
        const sync = () => {
          onScreen && tabVisible ? master.play() : master.pause()
        }
        const io = new IntersectionObserver(
          (entries) => {
            onScreen = entries[0].isIntersecting
            sync()
          },
          { threshold: 0.15 },
        )
        const onVisibility = () => {
          tabVisible = !document.hidden
          sync()
        }
        const ro = new ResizeObserver(measure)
        if (rootRef.current) {
          io.observe(rootRef.current)
          ro.observe(rootRef.current)
        }
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
          io.disconnect()
          ro.disconnect()
          document.removeEventListener('visibilitychange', onVisibility)
        }
      })

      // Reduced motion: render the first claim, fully settled, and never animate.
      mm.add('(prefers-reduced-motion: reduce)', () => {
        measure()
        gsap.set(content, { autoAlpha: 1, y: 0 })
        setClaim(FIRST)
        apply(FIRST, 1)
      })

      return () => mm.revert()
    },
    { scope: rootRef },
  )

  return (
    <div ref={rootRef} css={styles.root} role="img" aria-label={ARIA_LABEL}>
      <article css={styles.card} aria-hidden="true">
        <div ref={contentRef} css={styles.content}>
          <div css={styles.header}>
            <div>
              <div css={styles.eyebrow}>Incoming claim</div>
              <div ref={titleRef} css={styles.title}>
                {FIRST.id} · {FIRST.type}
              </div>
            </div>
            <div ref={statusRef} css={styles.pill}>
              Matching…
            </div>
          </div>

          <div css={styles.panel}>
            <div css={styles.ringWrap}>
              <svg
                css={styles.ringSvg}
                viewBox="0 0 150 150"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  css={styles.ringTrack}
                  cx="75"
                  cy="75"
                  r={RING_R}
                  fill="none"
                  strokeWidth="15"
                />
                <circle
                  ref={ringRef}
                  cx="75"
                  cy="75"
                  r={RING_R}
                  fill="none"
                  stroke={colorTokens.green100}
                  strokeWidth="15"
                  strokeLinecap="round"
                  strokeDasharray={`0 ${RING_C.toFixed(1)}`}
                  transform="rotate(-90 75 75)"
                />
              </svg>
              <div css={styles.ringCenter}>
                <div ref={scoreRef} css={styles.score}>
                  0
                </div>
                <div css={styles.scoreLabel}>SCORE</div>
              </div>
            </div>
            <div>
              <div css={styles.panelTitle}>AI claim score</div>
              <div css={styles.panelBody}>
                High-conversion, low-complexity. Powered by AnnieAi machine
                learning.
              </div>
            </div>
          </div>

          <div css={styles.partnersLabel}>Best-matched partners</div>
          <div css={styles.rows}>
            {FIRST.partners.map((p, i) => (
              <div
                key={p.name}
                ref={(el) => {
                  if (el) rowRefs.current[i] = el
                }}
                css={styles.row}
              >
                <div
                  ref={(el) => {
                    if (el) avatarRefs.current[i] = el
                  }}
                  css={[
                    styles.avatar,
                    css`
											background: ${TIERS[i].avatarBg};
											color: ${TIERS[i].avatarColor};
										`,
                  ]}
                >
                  {p.initials}
                </div>
                <div css={styles.rowMain}>
                  <div css={styles.rowTop}>
                    <div
                      ref={(el) => {
                        if (el) nameRefs.current[i] = el
                      }}
                      css={styles.name}
                    >
                      {p.name}
                    </div>
                    <span
                      ref={(el) => {
                        if (el) pctRefs.current[i] = el
                      }}
                      css={[
                        styles.pct,
                        css`
													color: ${TIERS[i].pctColor};
												`,
                      ]}
                    >
                      0%
                    </span>
                  </div>
                  <div css={styles.barTrack}>
                    <div
                      ref={(el) => {
                        if (el) fillRefs.current[i] = el
                      }}
                      css={[
                        styles.barFill,
                        css`
													background: ${TIERS[i].barColor};
												`,
                      ]}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div css={styles.footer}>
            <div css={styles.footerMeta}>
              Routed in <span ref={routedRef}>0.0</span>s · 3 partners evaluated
            </div>
            <div css={styles.footerAction}>Auto-assigned →</div>
          </div>
        </div>
      </article>
    </div>
  )
}
